/**
 * ProfileSideCard — network-style "you" card for the Community left rail.
 * Shows a small banner + avatar + name + headline, follower/following/posts
 * counts, and "Edit profile" + "View profile" affordances.
 *
 * Reads GET /api/network/profile/{id} (using current user's id).
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Users, Sparkles, Edit3, ArrowRight, Loader2 } from 'lucide-react';

const ProfileSideCard = () => {
  const { API, user } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!user?.id) return;
    axios.get(`${API}/network/profile/${user.id}`)
      .then((r) => { if (alive) setProfile(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [API, user?.id]);

  if (!user) return null;
  if (!profile) return (
    <div className="pro-card p-4 text-xs text-muted-foreground inline-flex items-center gap-2">
      <Loader2 className="w-3 h-3 animate-spin" /> loading…
    </div>
  );

  const handle = profile.handle || profile.id;

  // Profile strength — 8 fields, 1 point each. Users see a nudge until 100%.
  const filled = [
    !!profile.avatar_url,
    !!profile.headline,
    !!profile.bio,
    (profile.skills?.length || 0) >= 3,
    (profile.work_history?.length || 0) >= 1,
    !!profile.website,
    !!profile.location,
    !!profile.handle,
  ].filter(Boolean).length;
  const total = 8;
  const pct = Math.round((filled / total) * 100);
  const missing = [
    !profile.avatar_url    && 'photo',
    !profile.headline      && 'headline',
    !profile.bio           && 'about section',
    (profile.skills?.length || 0) < 3 && '3+ skills',
    (profile.work_history?.length || 0) < 1 && 'work history',
    !profile.website       && 'website',
    !profile.location      && 'location',
    !profile.handle        && 'custom handle',
  ].filter(Boolean).slice(0, 2);
  const strengthLabel = pct >= 100 ? 'All-star' : pct >= 75 ? 'Advanced' : pct >= 50 ? 'Intermediate' : 'Beginner';

  return (
    <aside className="pro-card overflow-hidden" data-testid="community-side-profile">
      {/* Banner */}
      <Link to={`/u/${handle}`} className="block h-14 bg-gradient-to-br from-primary/40 via-primary/20 to-transparent" />
      <div className="px-4 pb-4 -mt-8">
        <Link to={`/u/${handle}`} className="block h-14 w-14 rounded-full ring-4 ring-card bg-primary/20 text-primary flex items-center justify-center text-lg font-heading font-extrabold overflow-hidden">
          {profile.avatar_url
            ? <img src={profile.avatar_url} alt="" className="h-full w-full object-cover" />
            : (profile.name || '?').slice(0, 2).toUpperCase()}
        </Link>
        <Link to={`/u/${handle}`} className="mt-2 block">
          <div className="font-heading font-extrabold text-sm truncate hover:text-primary transition-colors">{profile.name}</div>
          {profile.headline && (
            <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{profile.headline}</div>
          )}
          {profile.location && (
            <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{profile.location}</div>
          )}
        </Link>
      </div>

      {/* Profile strength — only shown when < 100% */}
      {pct < 100 && (
        <Link
          to={`/u/${handle}?edit=1`}
          className="mx-3 mb-3 flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/10 p-2 transition-colors"
          data-testid="profile-strength"
        >
          <ProgressRing pct={pct} />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Profile · {strengthLabel}</div>
            <div className="text-[11px] text-foreground font-semibold">{pct}% complete</div>
            <div className="text-[10px] text-muted-foreground truncate">Add {missing.join(' & ')}</div>
          </div>
        </Link>
      )}

      {/* Stats strip */}
      <div className="border-t border-border grid grid-cols-3 divide-x divide-border text-center bg-muted/30">
        <StatCell label="Followers" value={profile.follower_count} testid="side-followers" />
        <StatCell label="Following" value={profile.following_count} testid="side-following" />
        <StatCell label="Posts"     value={profile.posts_count}     testid="side-posts"     />
      </div>

      {/* CTAs */}
      <div className="p-2 border-t border-border flex items-center gap-1">
        <Link
          to={`/u/${handle}`}
          className="flex-1 inline-flex items-center justify-center gap-1 rounded-full h-8 text-[11px] font-bold text-muted-foreground hover:bg-muted transition-colors"
          data-testid="side-view-profile"
        >
          <ArrowRight className="w-3 h-3" /> View
        </Link>
        <Link
          to={`/u/${handle}?edit=1`}
          className="flex-1 inline-flex items-center justify-center gap-1 rounded-full h-8 text-[11px] font-bold bg-primary/10 text-primary hover:bg-primary/15 transition-colors"
          data-testid="side-edit-profile"
        >
          <Edit3 className="w-3 h-3" /> Edit
        </Link>
      </div>
    </aside>
  );
};

const StatCell = ({ label, value, testid }) => (
  <div className="py-2" data-testid={testid}>
    <div className="font-heading text-sm font-extrabold text-foreground">{value ?? 0}</div>
    <div className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground">{label}</div>
  </div>
);

// SVG ring — cheap, no dep. 32px, 3px stroke, primary progress + muted track.
const ProgressRing = ({ pct }) => {
  const size = 34;
  const stroke = 3;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, pct)) / 100) * c;
  return (
    <svg width={size} height={size} className="flex-shrink-0" viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeOpacity="0.15" strokeWidth={stroke} className="text-primary" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        className="text-primary transition-all duration-500"
      />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" className="fill-primary font-bold" style={{ fontSize: 10 }}>
        {pct}%
      </text>
    </svg>
  );
};

export default ProfileSideCard;
